/**
 * Weapons.
 *
 * Content, not code: damage, reach and swing timing live here so combat can be
 * tuned without touching the systems that read them — the same rule as crops
 * and fish.
 *
 * Reach is in tiles and swing time in seconds, because those are the units the
 * design conversation happens in. The combat system converts.
 */

export type WeaponId =
  | 'pruningBlade'
  | 'ironSword'
  | 'rootcleaver'
  | 'tidewhisper'
  | 'emberbrand'
  | 'heartwoodMaul'
  | 'tidecaller';

export interface WeaponDefinition {
  readonly id: WeaponId;
  readonly name: string;
  /** Higher supersedes lower on the belt, as with every other tool family. */
  readonly tier: number;
  readonly damage: number;
  /** How far ahead of Katie the arc reaches, in tiles. */
  readonly reach: number;
  /** How long a swing lasts, in seconds. */
  readonly swingSeconds: number;
  /** What this becomes, once it has been carried far enough. */
  readonly evolvesTo?: WeaponId;
}

export const WEAPONS: Readonly<Record<WeaponId, WeaponDefinition>> = {
  // The starting blade is a farm tool that happens to cut: short, quick, and
  // never the reason a fight is won.
  pruningBlade: {
    id: 'pruningBlade',
    name: 'Pruning Blade',
    tier: 1,
    damage: 4,
    reach: 1,
    swingSeconds: 0.28,
  },
  ironSword: {
    id: 'ironSword',
    name: 'Iron Sword',
    tier: 2,
    damage: 9,
    reach: 1.5,
    swingSeconds: 0.32,
  },

  // The two forged blades are the point of the phase: they are not the Iron
  // Sword with bigger numbers, they are different questions. One asks whether
  // you can afford to commit to a swing; the other whether you can stay at the
  // right distance long enough for small hits to add up.
  //
  // Same tier on purpose. The belt retires by tier, so equal tiers mean owning
  // one never silently deletes the other — the choice stays a choice.
  rootcleaver: {
    id: 'rootcleaver',
    name: 'Rootcleaver',
    tier: 3,
    damage: 18,
    reach: 1.8,
    // Nearly twice the Iron Sword's swing. Missing with this hurts.
    swingSeconds: 0.58,
    evolvesTo: 'heartwoodMaul',
  },
  // Mira's own pattern, and the only blade in the game that has to be earned
  // from a person rather than found or forged from a table. Deliberately the
  // middle of the three: it asks nothing of you, which is its own answer to
  // the question the other two pose.
  emberbrand: {
    id: 'emberbrand',
    name: 'Emberbrand',
    tier: 3,
    damage: 11,
    reach: 2,
    swingSeconds: 0.36,
  },
  tidewhisper: {
    id: 'tidewhisper',
    name: 'Tidewhisper',
    tier: 3,
    damage: 6,
    // Longest reach in the game, and quick enough to chain before anything
    // closes the gap.
    reach: 2.4,
    swingSeconds: 0.19,
    evolvesTo: 'tidecaller',
  },

  // Evolved forms. Same tier as what they came from, deliberately: tier
  // governs retirement, and an evolution that outranked every other blade
  // would quietly destroy the one you did not evolve. Evolution already
  // consumes its source; it should not take anything else with it.
  // Both must beat a *fully grown* original, not the base one. Evolution
  // costs five levels of practice, so a form that merely beat level one would
  // be a downgrade dressed as a reward — which is exactly what the first
  // numbers here were, and what the test caught.
  heartwoodMaul: {
    id: 'heartwoodMaul',
    name: 'Heartwood Maul',
    tier: 3,
    damage: 34,
    reach: 2.1,
    swingSeconds: 0.54,
  },
  tidecaller: {
    id: 'tidecaller',
    name: 'Tidecaller',
    tier: 3,
    damage: 12,
    reach: 3.2,
    swingSeconds: 0.17,
  },
};

/**
 * Growth.
 *
 * A weapon earns its keep by being used, which is the Dark Cloud 2 idea the
 * README points at: the blade you carried through the dungeon should be better
 * than the identical one still sitting in the forge.
 *
 * Experience is damage dealt, so training a weapon is indistinguishable from
 * fighting with it. There is nothing to grind at and no second currency —
 * using the thing is the whole mechanism.
 */

export const MAX_WEAPON_LEVEL = 5;

/**
 * Total damage dealt to reach each level, starting at level 1.
 *
 * Cumulative rather than per-level so the table reads as a journey: a moth is
 * eight points, so level two is roughly fifteen of them.
 */
const LEVEL_THRESHOLDS: readonly number[] = [0, 120, 320, 640, 1100];

/**
 * What a level is worth, as fractions of the weapon's own numbers.
 *
 * Proportional on purpose. A flat bonus would make the Tidewhisper's six
 * damage grow faster in relative terms than the Rootcleaver's eighteen, and
 * quietly turn a choice of shape into a choice of which one levels better.
 */
export const GROWTH_PER_LEVEL = {
  damage: 0.14,
  reach: 0.03,
  /** Negative: a practised swing is a faster one. */
  swingSeconds: -0.025,
} as const;

/** The level a given amount of experience buys. */
export function levelForExperience(experience: number): number {
  let level = 1;
  for (let i = 1; i < LEVEL_THRESHOLDS.length; i++) {
    if (experience >= LEVEL_THRESHOLDS[i]!) level = i + 1;
  }
  return Math.min(MAX_WEAPON_LEVEL, level);
}

/** Experience still needed for the next level, or null at the ceiling. */
export function experienceToNext(experience: number): number | null {
  const level = levelForExperience(experience);
  if (level >= MAX_WEAPON_LEVEL) return null;
  return LEVEL_THRESHOLDS[level]! - experience;
}

/**
 * A weapon as it actually swings, once its levels are counted.
 *
 * Everything that reads weapon numbers goes through this rather than the raw
 * definition, so a level is a real change to the fight rather than a number on
 * a screen somewhere.
 */
export function grownWeapon(definition: WeaponDefinition, level: number): WeaponDefinition {
  const steps = Math.max(0, level - 1);
  if (steps === 0) return definition;

  return {
    ...definition,
    damage: Math.round(definition.damage * (1 + GROWTH_PER_LEVEL.damage * steps)),
    reach: definition.reach * (1 + GROWTH_PER_LEVEL.reach * steps),
    swingSeconds: definition.swingSeconds * (1 + GROWTH_PER_LEVEL.swingSeconds * steps),
  };
}

export const WEAPON_IDS = Object.keys(WEAPONS) as WeaponId[];

export function isWeaponId(value: unknown): value is WeaponId {
  return typeof value === 'string' && value in WEAPONS;
}
